/**
 * `/` — 注文落札状況 (Order / auction-win status).
 *
 * Phase-1 customer-portal landing page. Gives the customer an at-a-glance
 * status view of their orders (= won / purchased vehicles): how many sit at
 * each lifecycle stage, plus the most recent orders. It reuses the existing
 * `/vehicles` BFF data (PortalVehicleSummary[]) — no new backend endpoint —
 * and links each row to the vehicle detail.
 *
 * Labels are Japanese for the phase-1 rollout (the nav will be unified to
 * English later). The detailed per-vehicle list lives at /vehicles
 * (sidebar: 落札車両).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PortalVehicleSummary } from '@customer-portal/shared';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';
import { PurchaseRequestsSection } from '../components/purchase-requests/PurchaseRequestsSection';
import { PortalFeatureDisabledError, apiGet } from '../lib/api';
import { PortalApiError } from '../lib/apiClient';

interface ListEnvelope {
  ok: true;
  data: {
    items: PortalVehicleSummary[];
    total: number;
    limit: number;
    offset: number;
  };
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'success'; items: PortalVehicleSummary[]; total: number }
  | { kind: 'error'; code: string };

/** Lifecycle stages (mirror of VehiclesPage STATUS_OPTIONS) with JP labels. */
const STAGES: { match: (s: string) => boolean; label: string }[] = [
  { match: (s) => s.includes('preparing') || s.includes('inspection'), label: 'Preparing' },
  { match: (s) => s.includes('ready'), label: 'Ready to ship' },
  { match: (s) => s.includes('arranged'), label: 'Shipment arranged' },
  { match: (s) => s.includes('loaded'), label: 'Loaded' },
  { match: (s) => s.includes('transit'), label: 'In transit' },
  { match: (s) => s.includes('arrived'), label: 'Arrived at port' },
  { match: (s) => s.includes('delivered'), label: 'Delivered' },
];

function stageLabel(status: string): string {
  const s = (status || '').toLowerCase();
  const hit = STAGES.find((st) => st.match(s));
  return hit ? hit.label : status || '—';
}

export function OrderStatusPage(): JSX.Element {
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });

  useEffect(() => {
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    void (async () => {
      // Accumulate across pages OUTSIDE the try so the catch can tell a page-0
      // "feature disabled" 503 (→ empty state) apart from a transient upstream
      // 503 that lands AFTER we already have orders (→ error state, never
      // "no orders" — which would also drop the pages already fetched).
      const all: PortalVehicleSummary[] = [];
      let total = 0;
      try {
        // The status breakdown must reflect *all* of the customer's orders, not
        // just the first page: the /vehicles BFF caps `limit` at 100, so a
        // single request would under-count any status on a later page. Page
        // through with a bounded loop and aggregate so the cards match `total`.
        const PAGE_SIZE = 100;
        const MAX_PAGES = 50; // safety cap (5,000 orders) — far beyond any customer
        for (let page = 0; page < MAX_PAGES; page += 1) {
          const offset = page * PAGE_SIZE;
          const res = await apiGet<ListEnvelope>(
            `/vehicles?limit=${PAGE_SIZE}&offset=${offset}`,
            ctrl.signal,
          );
          const items = res.data?.items ?? [];
          total = res.data?.total ?? all.length + items.length;
          all.push(...items);
          if (items.length < PAGE_SIZE) break; // last (partial) page reached
          if (all.length >= total) break; // collected everything the API reports
        }
        setLoad({ kind: 'success', items: all, total });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalFeatureDisabledError && all.length === 0) {
          // Only when the VERY FIRST page 503s: the vehicles BFF is not wired
          // in this env (feature disabled). Render the clean empty state ("no
          // orders yet"); real data appears once the upstream is wired.
          setLoad({ kind: 'success', items: [], total: 0 });
          return;
        }
        // Anything else — a genuine upstream failure, OR a transient 503 that
        // hit a LATER page after we already collected orders — is a real
        // error. Never mask it as "no orders". Surface the error state and
        // keep it observable (console → telemetry). Only the error code is
        // logged (no tokens / PII).
        const code =
          err instanceof PortalApiError
            ? err.code
            : err instanceof PortalFeatureDisabledError
              ? err.code
              : 'UNKNOWN';
        console.error('[OrderStatusPage] vehicles fetch failed', code);
        setLoad({ kind: 'error', code });
      }
    })();
    return () => ctrl.abort();
  }, []);

  // Count orders per lifecycle stage for the summary cards.
  const stageCounts = useMemo(() => {
    if (load.kind !== 'success') return [] as { label: string; count: number }[];
    const counts = new Map<string, number>();
    for (const v of load.items) {
      const label = stageLabel(v.status);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }
    // Keep STAGES order, then append any unmatched statuses.
    const ordered = STAGES.map((s) => ({ label: s.label, count: counts.get(s.label) ?? 0 }));
    for (const [label, count] of counts) {
      if (!STAGES.some((s) => s.label === label)) ordered.push({ label, count });
    }
    return ordered;
  }, [load]);

  // Most recent 10 orders for the at-a-glance list.
  const recent = useMemo(() => {
    if (load.kind !== 'success') return [] as PortalVehicleSummary[];
    return load.items.slice(0, 10);
  }, [load]);

  return (
    <section data-testid="order-status-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>
          Order &amp; winning-bid status
        </h1>
        <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
          Track your purchase requests and the vehicles you have won in one place.
        </p>
      </header>

      <PurchaseRequestsSection />

      {load.kind === 'idle' || load.kind === 'loading' ? (
        <div aria-busy="true" data-testid="order-status-loading">
          <Spinner block label="Loading" />
        </div>
      ) : null}

      {load.kind === 'error' ? (
        <div data-testid="order-status-error">
          <ErrorState
            title="Failed to load"
            description={`Error code: ${load.code}`}
            onRetry={() => window.location.reload()}
          />
        </div>
      ) : null}

      {load.kind === 'success' && load.items.length === 0 ? (
        <div data-testid="order-status-empty">
          <EmptyState
            title="No orders or winning bids yet"
            description="Vehicles you win at auction or purchase will appear here as they are processed."
          />
        </div>
      ) : null}

      {load.kind === 'success' && load.items.length > 0 ? (
        <>
          <Card aria-label="Status overview">
            <CardHeader>
              <CardTitle level={2}>Count by status ({load.total} total)</CardTitle>
            </CardHeader>
            <CardBody>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(8.5rem, 1fr))',
                  gap: '0.75rem',
                }}
              >
                {stageCounts.map((s) => (
                  <div
                    key={s.label}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.5rem',
                      padding: '0.75rem 0.875rem',
                      background: s.count > 0 ? '#fff' : '#fafafa',
                    }}
                  >
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>
                      {s.label}
                    </div>
                    <div
                      style={{
                        fontSize: '1.375rem',
                        fontWeight: 700,
                        color: s.count > 0 ? '#111827' : '#9ca3af',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.count}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          <Card aria-label="Recent orders & winning bids">
            <CardHeader>
              <CardTitle level={2}>Recent orders &amp; winning bids</CardTitle>
            </CardHeader>
            <CardBody>
              <Table caption={`Latest ${recent.length} of ${load.total} total`}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Vehicle</TableHeaderCell>
                    <TableHeaderCell>Chassis number</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: 'right' }}>Details</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <div style={{ fontWeight: 600, color: '#111827' }}>
                          {v.maker_name} {v.model_name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {v.model_year != null ? `${v.model_year}` : '—'}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                        {v.chassis_number}
                      </TableCell>
                      <TableCell>
                        <OrderStatusPill status={v.status} />
                      </TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        <Link
                          to={`/vehicles/${v.id}`}
                          style={{
                            color: '#1f4ed8',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            textDecoration: 'none',
                          }}
                          data-testid={`order-link-${v.id}`}
                        >
                          Details →
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.8125rem' }}>
                <Link
                  to="/vehicles"
                  style={{ color: '#1f4ed8', fontWeight: 600, textDecoration: 'none' }}
                >
                  View all winning-bid vehicles →
                </Link>
              </p>
            </CardBody>
          </Card>
        </>
      ) : null}
    </section>
  );
}

function OrderStatusPill({ status }: { status: string }): JSX.Element {
  const s = (status || '').toLowerCase();
  let bg = '#f3f4f6';
  let color = '#374151';
  if (s.includes('transit')) {
    bg = '#dbeafe';
    color = '#1e40af';
  } else if (s.includes('arrived') || s.includes('delivered')) {
    bg = '#d1fae5';
    color = '#065f46';
  } else if (s.includes('ready') || s.includes('arranged') || s.includes('loaded')) {
    bg = '#e0e7ff';
    color = '#3730a3';
  } else if (s.includes('preparing') || s.includes('inspection')) {
    bg = '#fef3c7';
    color = '#92400e';
  }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.125rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.6875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        background: bg,
        color,
      }}
    >
      {stageLabel(status)}
    </span>
  );
}
